import React, { useState, useRef, useEffect } from 'react';
import Span from '../Texts/Span/Span';
import Icon from '../Icon/Icon';
import PropTypes from 'prop-types';
import * as S from './MoreActions.styles';

/**
 *
 * @param items Objeto de configuracion para el armado del desplegable con acciones.
 * El objeto tiene la forma:
 *
 * [
 *         {
 *           title: String, es el titulo de la opcion,
 *           action: function, es la funcion que se dispara cuando se clickea en la opcion,
 *           disabled: Boolean, parametro para habilitar/deshabilitar la opcion,
 *           icon: String, parametro para indicar el atributo name de Icon (icono es opcional)
 *           hidden: Boolean, parametro para definir si se quiere renderizar el item
 *          },
 * ]
 * Ejemplo:
 * const items = [
 *         {
 *           title: 'Editar',
 *           action: () => {},
 *           disabled: true,
 *         },
 *         {
 *           title: 'Eliminar',
 *           variant: 'danger',
 *           action: () => {},
 *         },
 *         {
 *           title: 'Item no deseado',
 *           hidden: true
 *         },
 *       ];
 * @return {JSX.Element}
 * @constructor
 */

const MoreActions = ({ children, items, onToggle, position }) => {
  const [open, setOpen] = useState(false);
  const actionableOptionsRef = useRef();
  const dropdownRef = useRef();

  useEffect(() => {
    const externalEventHandler = (e) => {
      if (!open) return;

      const node = dropdownRef.current;

      if (node && node.contains(e.target)) {
        return;
      }

      setOpen(false);
    };

    open
      ? document.addEventListener('click', externalEventHandler)
      : document.removeEventListener('click', externalEventHandler);

    return () => {
      document.removeEventListener('click', externalEventHandler);
    };
  }, [open]);

  const toggle = () => {
    const safeOpen = !open;
    setOpen(safeOpen);
    onToggle && onToggle(safeOpen);
  };

  useEffect(() => {
    if (open) {
      actionableOptionsRef.current?.focus();
    }
  }, [open]);

  const renderDropdownContent = () => {
    return items
      ? items.map(
          (item, index) =>
            !item?.hidden && (
              <S.Action key={item.title}>
                {item?.icon && (
                  <Icon
                    name={item.icon}
                    variant={item.iconVariant || 'neutral'}
                    modifier={item.modifier || 'weak'}
                    disabled={item.disabled}
                  />
                )}
                <S.Item
                  data-testid={`item-${index}`}
                  onClick={() => {
                    callItemAction(item.action);
                  }}
                  disabled={item.disabled}
                >
                  <Span
                    variant={item.variant || 'neutral'}
                    modifier={item.disabled ? 'weak' : 'strong'}
                    type='2'
                  >
                    {item.title}
                  </Span>
                </S.Item>
              </S.Action>
            )
        )
      : children;
  };

  const callItemAction = (action) => {
    setOpen(false);
    onToggle && onToggle(false);
    action();
  };

  return (
    <S.Wrapper ref={dropdownRef}>
      <Icon name='more_vertical' onClick={toggle} cursor={true} />
      <S.WrapperElements>
        {open && (
          <S.Element
            ref={actionableOptionsRef}
            tabIndex={0}
            position={position}
          >
            {renderDropdownContent()}
          </S.Element>
        )}
      </S.WrapperElements>
    </S.Wrapper>
  );
};

MoreActions.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      action: PropTypes.func,
      disabled: PropTypes.bool,
      icon: PropTypes.string,
      hidden: PropTypes.bool,
    })
  ),
  onToggle: PropTypes.func,
  position: PropTypes.oneOf(['left', 'right']),
};

export default MoreActions;
